import React from 'react';
import { MapPin } from 'lucide-react';

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' },
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const AddressForm = ({ address, onChange, errors = {} }) => {
  const handleChange = (field, value) => {
    onChange({ ...address, [field]: value });
  };

  const inputClass = (field) => `
    w-full px-3 py-2 border rounded-lg text-sm
    ${errors[field] ? 'border-red-500 bg-red-50' : 'border-gray-300'}
    focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-transparent
  `;

  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-ocean-900 flex items-center gap-2">
        <MapPin className="w-4 h-4" />
        Shipping Address
      </h4>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
        <input
          type="text"
          value={address.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
          className={inputClass('name')}
          placeholder="John Smith"
        />
        {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
        <input
          type="email"
          value={address.email || ''}
          onChange={(e) => handleChange('email', e.target.value)}
          className={inputClass('email')}
          placeholder="john@example.com"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Address Line 1 *</label>
        <input
          type="text"
          value={address.line1 || ''}
          onChange={(e) => handleChange('line1', e.target.value)}
          className={inputClass('line1')}
          placeholder="123 Main Street"
        />
        {errors.line1 && <p className="text-xs text-red-600 mt-1">{errors.line1}</p>}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Address Line 2</label>
        <input
          type="text"
          value={address.line2 || ''}
          onChange={(e) => handleChange('line2', e.target.value)}
          className={inputClass('line2')}
          placeholder="Apt 4B (optional)"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">City *</label>
          <input
            type="text"
            value={address.city || ''}
            onChange={(e) => handleChange('city', e.target.value)}
            className={inputClass('city')}
            placeholder="New York"
          />
          {errors.city && <p className="text-xs text-red-600 mt-1">{errors.city}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {address.countryCode === 'US' ? 'State *' : 'State/Province'}
          </label>
          {address.countryCode === 'US' ? (
            <select
              value={address.state || ''}
              onChange={(e) => handleChange('state', e.target.value)}
              className={inputClass('state')}
            >
              <option value="">Select...</option>
              {US_STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={address.state || ''}
              onChange={(e) => handleChange('state', e.target.value)}
              className={inputClass('state')}
              placeholder="Province"
            />
          )}
          {errors.state && <p className="text-xs text-red-600 mt-1">{errors.state}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Postal Code *</label>
          <input
            type="text"
            value={address.postalCode || ''}
            onChange={(e) => handleChange('postalCode', e.target.value)}
            className={inputClass('postalCode')}
            placeholder="10001"
          />
          {errors.postalCode && <p className="text-xs text-red-600 mt-1">{errors.postalCode}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Country *</label>
          <select
            value={address.countryCode || 'US'}
            onChange={(e) => handleChange('countryCode', e.target.value)}
            className={inputClass('countryCode')}
          >
            {COUNTRIES.map(country => (
              <option key={country.code} value={country.code}>{country.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Phone (for delivery)</label>
        <input
          type="tel"
          value={address.phone || ''}
          onChange={(e) => handleChange('phone', e.target.value)}
          className={inputClass('phone')}
          placeholder="+1 555 123 4567"
        />
      </div>
    </div>
  );
};

export default AddressForm;
